import { format, isSameYear, isValid, startOfDay, formatISO } from 'date-fns';
import { sortBy, get, isArray, isPlainObject, isEmpty, uniq } from 'lodash';
import { stringify } from 'qs-esm';

import zonedDate from '../utils/zoned-date';
import favorites from './favorites';
import { getCustomerConcerts } from './blocksoffice';
import { getAbsoluteUrl } from '../utils/environment';

let _seasonsPromise = null;
let _instancesPromise = null;
let _instancePromises = {}
let _seasonInstancesPromises = {}
let _eventsPromise = null;
let _seriesPromise = null;
let _individualSeriesPromises = {}
let _peoplePromise = null;
let _personPromises = {}
let _seasonPromises = {}
let _transportationGroupPromise = null;
let _collectionPromises = {}

const EVENT_INSTANCE_FIELDS = [
    'startDate',
    'slug',
    'richTitle_html',
    'title',
    'description',
    'image',
    'tenant.slug',
    'tessitura.performanceId',
    'booking',

    'attributes.custom.prefix',
    'attributes.custom.second_billing_html',
    'attributes.custom.third_billing_html',
    'attributes.custom.suffix',
    'attributes.custom.gate_time',
    'attributes.custom.price_from',
    'attributes.custom.parking_system_type',
    'attributes.custom.park_and_ride_id',
    'attributes.custom.bowl_shuttle_id',
    'attributes.custom.transportation_group',
    'attributes.custom.allow_favoriting',
    'attributes.custom.hide_from_calendar',
    'attributes.custom.external_link_indicator',
    'attributes.custom.calendar_flags',
    'attributes.custom.genres',
    'attributes.custom.categories',
    'attributes.custom.program',
    'attributes.custom.artists',

    'venue.title',
    'venue.attributes.custom.transportation_group',

    'series.attributes.custom.subscription_package_id',

    'event.startDate',
    'event.endDate',
    'event.richTitle_html',
    'event.title',
    'event.description',
    'event.image',
    'event.booking',

    'event.season.attributes.custom.create_your_own_package_id',
    'event.season.attributes.custom.use_best_available',
    'event.season.attributes.custom.allow_favoriting',

    'event.attributes.custom.prefix',
    'event.attributes.custom.second_billing_html',
    'event.attributes.custom.third_billing_html',
    'event.attributes.custom.suffix',
    'event.attributes.custom.gate_time',
    'event.attributes.custom.price_from',
    'event.attributes.custom.parking_system_type',
    'event.attributes.custom.allow_favoriting',
    'event.attributes.custom.hide_from_calendar',
    'event.attributes.custom.external_link_indicator',
    'event.attributes.custom.calendar_flags',
    'event.attributes.custom.is_lease_event',
    'event.attributes.custom.genres',
    'event.attributes.custom.categories',
    'event.attributes.custom.program',
    'event.attributes.custom.artists',

    'event.venue.title',
    'event.venue.attributes.custom.transportation_group',
]

const today = startOfDay(zonedDate());

async function fetchJson(url) {
    const response = await fetch(url); // eslint-disable-line

    if (!response.ok) {
        throw new Error(`${url} responded with ${response.status}`);
    }

    return response.json();
}

// Drop a failed request from its cache so the next call retries rather than
// replaying the rejection for the rest of the page session
function forgetOnFailure(promise, forget) {
    return promise.catch(error => {
        forget();

        throw error;
    });
}

// Process records one at a time so a single malformed record is logged and
// skipped instead of rejecting the whole list
function processEach(docs, processor) {
    return docs.filter(doc => {
        try {
            processor(doc);

            return true;
        } catch (error) {
            console.error(`Skipping ${doc?.id ?? 'record'}, it could not be processed`, error);

            return false;
        }
    });
}

async function getPaginatedList(resource, params = {}) {
    const query = {
        limit: 50,
        ...params,
    }

    let docs = [];
    let page = 1;

    do {
        const stringifiedQuery = stringify({ ...query, page: page }, { addQueryPrefix: true });

        const response = await fetchJson(`${resource}${stringifiedQuery}`);

        docs = [ ...docs, ...(response.docs || []) ];

        page = response.nextPage;
    } while (page);

    return docs;
}

export async function getUpcomingInstances() {
    if (_instancesPromise) {
        return _instancesPromise;
    }

    const params = {
        depth: 3,
        limit: 500,
        // select: EVENT_INSTANCE_FIELDS.join(','),
    }

    _instancesPromise = forgetOnFailure(getPaginatedList('/api/event-instances.json', params).then(response => {
        response = processEach(response, processInstance).filter(item => {
            return +item.startDate > +today;
        });

        return sortBy(response, item => {
            return +item.startDate;
        });
    }), () => {
        _instancesPromise = null;
    });

    return _instancesPromise;
}

export async function getInstancesForSeason(id) {
    if (id in _seasonInstancesPromises) {
        return _seasonInstancesPromises[id];
    }

    const params = {
        seasonId: id,
        showPast: true,
        depth: 3,
        // select: EVENT_INSTANCE_FIELDS.join(','),
    }

    _seasonInstancesPromises[id] = forgetOnFailure(getPaginatedList('/api/event-instances.json', params).then(response => {
        return sortBy(processEach(response, processInstance), item => {
            return +item.startDate;
        });
    }), () => {
        delete _seasonInstancesPromises[id];
    });

    return _seasonInstancesPromises[id];
}

export async function getEvents() {
    if (_eventsPromise) {
        return _eventsPromise;
    }

    _eventsPromise = forgetOnFailure(getPaginatedList('/api/events.json').then(docs => {
        return sortBy(processEach(docs, processEvent), item => {
            return +item.startDate;
        });
    }), () => {
        _eventsPromise = null;
    });

    return _eventsPromise;
}

export async function getSeasons() {
    if (_seasonsPromise) {
        return _seasonsPromise;
    }

    _seasonsPromise = forgetOnFailure(getPaginatedList('/api/seasons.json').then(docs => {
        return sortBy(processEach(docs, processSeason), item => {
            return +item.startDate;
        });
    }), () => {
        _seasonsPromise = null;
    });

    return _seasonsPromise;
}

export async function getSeasonById(id) {
    if (id in _seasonPromises) {
        return _seasonPromises[id];
    }

    _seasonPromises[id] = forgetOnFailure(fetchJson(`/api/seasons/${id}`).then(response => {
        return processSeason(response.doc);
    }), () => {
        delete _seasonPromises[id];
    });

    return _seasonPromises[id];
}

export async function getSeries() {
    if (_seriesPromise) {
        return _seriesPromise;
    }

    _seriesPromise = forgetOnFailure(getPaginatedList('/api/series.json').then(docs => {
        return sortBy(processEach(docs, processSeries), item => {
            return +item.startDate;
        });
    }), () => {
        _seriesPromise = null;
    });

    return _seriesPromise;
}

export async function getSeriesById(id) {
    if (id in _individualSeriesPromises) {
        return _individualSeriesPromises[id];
    }

    _individualSeriesPromises[id] = forgetOnFailure((async () => {
        const seriesResponse = await fetchJson(`/api/series/${id}`).then(response => {
            return response.doc;
        });

        let seasonResponse = null;

        if (seriesResponse?.['event-instances']?.docs?.length && seriesResponse['event-instances'].docs[0]?.event?.season) {
            seasonResponse = await getSeasonById(seriesResponse['event-instances'].docs[0].event.season);
        }

        processSeries(seriesResponse, seasonResponse);

        return seriesResponse;
    })(), () => {
        delete _individualSeriesPromises[id];
    });

    return _individualSeriesPromises[id];
}

export async function getCollectionInstances(id) {
    if (id in _collectionPromises) {
        return _collectionPromises[id];
    }

    _collectionPromises[id] = forgetOnFailure((async () => {
        const collectionResponse = await fetchJson(`/api/smart-collections/${id}`);

        if (!collectionResponse.doc) {
            throw new Error(`Smart collection ${id} has no doc`);
        }

        let promise = Promise.resolve([]);

        if (collectionResponse.doc.targetCollection === 'events') {
            const eventIds = uniq((collectionResponse.docs || []).map(item => {
                return item.id;
            }));

            if (eventIds.length) {
                promise = fetchJson(`/api/event-instances.json?where%5Bevent%5D%5Bin%5D=${eventIds.join(',')}&showPast=true&depth=3`).then(response => {
                    return response.docs || [];
                });
            }
        } else if (collectionResponse.doc.targetCollection === 'event-instances') {
            const instanceIds = uniq((collectionResponse.docs || []).map(item => {
                return item.id;
            }));

            if (instanceIds.length) {
                promise = fetchJson(`/api/event-instances.json?where%5Bid%5D%5Bin%5D=${instanceIds.join(',')}&showPast=true&depth=3`).then(response => {
                    return response.docs || [];
                });
            }
        }

        return promise.then(response => {
            return sortBy(processEach(response, processInstance), item => {
                return +item.startDate;
            });
        });
    })(), () => {
        delete _collectionPromises[id];
    });

    return _collectionPromises[id];
}

export async function getPeople() {
    if (_peoplePromise) {
        return _peoplePromise;
    }

    _peoplePromise = forgetOnFailure(getPaginatedList('/api/people.json'), () => {
        _peoplePromise = null;
    });

    return _peoplePromise;
}

export async function getPerson(id) {
    if (id in _personPromises) {
        return _personPromises[id];
    }

    _personPromises[id] = forgetOnFailure(fetchJson(`/api/people/${id}`).then(response => {
        return response.doc;
    }), () => {
        delete _personPromises[id];
    });

    return _personPromises[id];
}

export async function getTransportationGroups() {
    if (_transportationGroupPromise) {
        return _transportationGroupPromise;
    }

    _transportationGroupPromise = forgetOnFailure(Promise.all([
        getPaginatedList('/api/custom-objects/transportation-group'),
        getPaginatedList('/api/custom-objects/transportation'),
    ]).then(([ transportationGroups, transportation ]) => {
        return transportationGroups.map(transportationGroup => {
            transportationGroup.locations = toArray(transportationGroup.locations).map(location => {
                if (isPlainObject(location)) {
                    return location;
                }

                return transportation.find(item => {
                    return item.id === location;
                });
            }).filter(location => location);

            return transportationGroup;
        })
    }), () => {
        _transportationGroupPromise = null;
    });

    return _transportationGroupPromise;
}

export async function getInstanceById(id) {
    if (id in _instancePromises) {
        return _instancePromises[id];
    }

    _instancePromises[id] = forgetOnFailure(fetchJson(`/api/event-instances/${id}?depth=3`).then(response => {
        processInstance(response.doc);

        return response.doc;
    }), () => {
        delete _instancePromises[id];
    });

    return _instancePromises[id];
}

export function updateFavoriteFilter(instance) {
    if (!instance.tessitura.performanceId) {
        return;
    }

    instance.filters.isFavorited = favorites.isFavoritePerformance({ id: instance.tessitura.performanceId }) ? 'Yes' : null;
}

// lodash get() only falls back for undefined, so a null from the API would
// still reach .length/.map. Anything that isn't an array becomes []
function toArray(value) {
    return isArray(value) ? value : [];
}

function titles(items) {
    return toArray(items).map(item => item?.title).filter(title => title);
}

function processEvent(event) {
    event.startDate = zonedDate(event.startDate);
    event.endDate = zonedDate(event.endDate);
    event.url = getAbsoluteUrl(event, 'event');
    event.image = event.image || window.AppConfig.fallbackImage;

    event.calendarFlags = titles(event.attributes?.custom?.calendar_flags);
}

function processInstance(instance) {
    if (!isPlainObject(instance.event)) {
        throw new Error(`Instance ${instance.id} has no event`);
    }

    if (!instance.startDate) {
        throw new Error(`Instance ${instance.id} has no start date`);
    }

    instance.startDate = zonedDate(instance.startDate);

    if (!isValid(instance.startDate)) {
        throw new Error(`Instance ${instance.id} has an invalid start date`);
    }

    instance.endDate = instance.attributes?.custom?.end_date ?
        zonedDate(instance.attributes?.custom?.end_date) :
        null;

    instance.hideStartTime = instance.hideStartTime || instance.event?.hideStartTime || false;

    instance.startDateIso = formatISO(instance.startDate, { rerepresentation: 'full' });
    instance.startDateSeparate = {
        dayMonth: format(instance.startDate, 'EEE MMM'),
        date: format(instance.startDate, 'd'),
        time: format(instance.startDate, instance.startDate.getMinutes() ? 'h:mmaa' : 'haa'),
    }

    processEvent(instance.event, 'event');

    // Instances without a Tessitura link (custom booking only) still list
    instance.tessitura = instance.tessitura || {};
    instance.tessitura.performanceId = parseInt(instance.tessitura.performanceId, 10);

    instance.bookingParams = {
        performanceId: instance.tessitura.performanceId,
        seriesId: instance.series?.[0]?.attributes?.custom?.subscription_package_id,
        createYourOwnId: instance.event.season?.attributes?.custom?.create_your_own_package_id,
        useBestAvailableForSeries: instance.event.season?.attributes?.custom?.use_best_available,
        useProgressiveCreateYourOwn: true, //instance.tenant.slug === 'laphil',
    }

    // This is a little annoying to do here, but it's cleaner
    const eventBookingUrl = get(instance, 'event.booking.bookingLinkURL');
    const instanceBookingUrl = get(instance, 'booking.bookingLinkURL');

    instance.customBooking = {
        bookingLabel: get(instance, 'booking.bookingLinkLabel') || get(instance, 'event.booking.bookingLinkLabel'),
        bookingUrl: null,
    }

    if (instanceBookingUrl) {
        instance.customBooking.bookingUrl = instanceBookingUrl;
    } else if (eventBookingUrl) {
        instance.customBooking.bookingUrl = eventBookingUrl;
    }

    instance.prefix = instance.attributes?.custom?.prefix?.trim() || instance.event.attributes?.custom?.prefix?.trim();
    instance.title = instance.title?.trim() || instance.event.title?.trim();
    instance.title_html = instance.richTitle_html?.trim() || instance.event.richTitle_html?.trim();
    instance.second_billing_html = instance.attributes?.custom?.second_billing_html?.trim() || instance.event.attributes?.custom?.second_billing_html?.trim();
    instance.third_billing_html = instance.attributes?.custom?.third_billing_html?.trim() || instance.event.attributes?.custom?.third_billing_html?.trim();
    instance.suffix = instance.attributes?.custom?.suffix?.trim() || instance.event.attributes?.custom?.suffix?.trim();
    instance.description = instance.description?.trim() || instance.event.description?.trim();
    instance.gate_time = instance.attributes?.custom?.gate_time || instance.event.attributes?.custom?.gate_time;
    instance.price_from = instance.attributes?.custom?.price_from || instance.event.attributes?.custom?.price_from;
    instance.image = instance.image || instance.event.image || window.AppConfig.fallbackImage;
    instance.url = getAbsoluteUrl(instance, 'instance');

    instance.parking_system_type = instance.attributes?.custom?.parking_system_type || instance.event.attributes?.custom?.parking_system_type;

    instance.venue = instance.venue || instance.event.venue;

    const transportationGroup = instance?.venue?.attributes?.custom?.transportation_group || instance.attributes?.custom?.transportation_group;

    instance.transportation_group = transportationGroup?.id || transportationGroup;

    instance.allow_favoriting = instance.event.season?.attributes?.custom?.allow_favoriting;

    if (instance.event.attributes?.custom?.allow_favoriting === 'Yes') {
        instance.allow_favoriting = true;
    } else if (instance.event.attributes?.custom?.allow_favoriting === 'No') {
        instance.allow_favoriting = false;
    }

    if (instance.attributes?.custom?.allow_favoriting === 'Yes') {
        instance.allow_favoriting = true;
    } else if (instance.attributes?.custom?.allow_favoriting === 'No') {
        instance.allow_favoriting = false;
    }

    instance.hide_from_calendar = instance.event.attributes?.custom?.hide_from_calendar;

    if (instance.attributes?.custom?.hide_from_calendar === 'Yes') {
        instance.hide_from_calendar = true;
    } else if (instance.attributes?.custom?.hide_from_calendar === 'No') {
        instance.hide_from_calendar = false;
    }

    instance.external_link_indicator = instance.event.attributes?.custom?.external_link_indicator === 'Yes';

    if (instance.attributes?.custom?.external_link_indicator === 'Yes') {
        instance.external_link_indicator = true;
    } else if (instance.attributes?.custom?.external_link_indicator === 'No') {
        instance.external_link_indicator = false;
    }

    // Normalise the f out of these
    // @todo do this properly right now it hacky
    normaliseProgram(instance);
    normaliseArtists(instance);

    const instanceGenres = toArray(instance.attributes?.custom?.genres);
    const eventGenres = toArray(instance.event.attributes?.custom?.genres);
    const instanceCategories = toArray(instance.attributes?.custom?.categories);
    const eventCategories = toArray(instance.event.attributes?.custom?.categories);

    const genres = instanceGenres.length ? instanceGenres : eventGenres;
    const categories = instanceCategories.length ? instanceCategories : eventCategories;

    instance.filters = {
        genres: titles(genres),
        categories: titles(categories),
        timeOfDay: instance.startDate.getHours() > 18 ? 'Evening' : 'Matinee',
        dayOfWeek: format(instance.startDate, 'eeee'),
        priceFrom: parseInt(instance.price_from, 10),
    }

    updateFavoriteFilter(instance);

    instance.isLeaseEvent = instance.event.attributes?.custom?.is_lease_event;

    const instanceFlag = toArray(instance.attributes?.custom?.calendar_flags);
    const eventFlags = toArray(instance.event.attributes?.custom?.calendar_flags);

    instance.calendarFlags = titles(instanceFlag.length ? instanceFlag : eventFlags);

    instance.isAttending = getCustomerConcerts().indexOf(parseInt(instance.tessitura.performanceId, 10)) !== -1;

    instance._auxiliary = {
        groupByDay: format(instance.startDate, 'yyyy-MM-dd'),
        groupByMonth: format(instance.startDate, 'yyyy-MM'),
        groupNameMonth: isSameYear(today, instance.startDate) ? format(instance.startDate, 'MMMM') : format(instance.startDate, 'MMMM yyyy'),
    }
}

function processSeason(season) {
    season.startDate = zonedDate(season.startDate);
    season.endDate = zonedDate(season.endDate);
    season.isInPast = +season.endDate < +today;
    season.hasEvents = season.events?.docs?.length ? true : false;
    season.image = season.image || window.AppConfig.fallbackImage;

    return season;
}

function processSeries(series, season) {
    series.startDate = zonedDate(series.startDate);
    series.endDate = zonedDate(series.endDate || series.startDate);
    series.url = getAbsoluteUrl(series, 'series');
    series.image = series.image || window.AppConfig.fallbackImage;

    series.noLongerAvailable = get(series, 'attributes.custom.no_longer_available', false);

    series.event_instances = sortBy(toArray(series['event-instances']?.docs).filter(instance => instance).map(instance => {
        instance.startDate = zonedDate(instance.startDate);

        return instance;
    }), instance => {
        return +instance.startDate;
    });

    series.season = season;

    const packageTypes = get(series, 'attributes.custom.package_types', []);

    series.filters = {
        types: isArray(packageTypes) ? packageTypes.map(type => {
            return type.title;
        }) : [],
        dayOfWeek: series.event_instances.map(instance => {
            return format(instance.startDate, 'eeee');
        }),
        totalPerformances: series.event_instances.length,
        timeOfDay: series.event_instances.map(instance => {
            return instance.startDate.getHours() > 18 ? 'Evening' : 'Matinee';
        }),
        featuring: [],
        priceFrom: parseInt(series.attributes?.custom?.price_from, 10),
        // genres: Array.isArray(genres) ? genres : [],
        // categories: Array.isArray(categories) ? categories : [],
    }
}

function normaliseProgram(instance) {
    const instanceProgram = toArray(instance.attributes?.custom?.program);
    const eventProgram = toArray(instance.event?.attributes?.custom?.program);

    const program = instanceProgram.length ? instanceProgram : eventProgram;

    // Set this so fe doesn't break
    instance.program = [];

    for (const section of program) {
        for (const item of toArray(section?.pieces)) {
            if (!item?.piece?.id) {
                continue;
            }

            const composers = [];
            const arrangers = [];

            if (isArray(item.piece.creator)) {
                for (const creator of item.piece.creator) {
                    if (!creator) {
                        continue;
                    }

                    let url = creator.url;

                    if (!creator.hasBiography && !creator.hasBlocks) {
                        url = false;
                    }

                    composers.push({
                        id: creator.id,
                        name: creator.name,
                        firstName: creator.firstName,
                        lastName: creator.lastName,
                        attributes: {
                            custom: {
                                is_deceased: creator?.attributes?.custom?.is_deceased
                            },
                        },
                        url,
                    });
                }
            }

            if (item.piece.attributes?.custom?.association && isArray(item.piece.attributes.custom.association)) {
                for (const association of item.piece.attributes.custom.association) {
                    if (association?.type?.id === '69b2e875c3677f16e05eeae3' && association.artist) {
                        let url = association.artist.url;

                        if (!association.artist.hasBiography && !association.artist.hasBlocks) {
                            url = false;
                        }

                        arrangers.push({
                            id: association.artist.id,
                            name: association.artist.name,
                            firstName: association.artist.firstName,
                            lastName: association.artist.lastName,
                            attributes: {
                                custom: {
                                    is_deceased: association.artist?.attributes?.custom?.is_deceased
                                },
                            },
                            url,
                        });
                    }
                }
            }

            let url = item.piece.url;

            const hasContent = !isEmpty(item.piece.attributes?.custom?.quick_look?.content_html)
                || !isEmpty(item.piece.attributes?.custom?.explore_further?.more_content_html);
            if (!item.piece.hasBlocks && !hasContent) {
                url = false;
            }

            instance.program.push({
                composers,
                arrangers,
                title: item.piece.title,
                url,
                isIntermission: item.piece.attributes?.custom?.is_intermission,
            });
        }
    }
}

function normaliseArtists(instance) {
    const instanceArtists = toArray(instance.attributes?.custom?.artists);
    const eventArtists = toArray(instance.event?.attributes?.custom?.artists);

    const artists = instanceArtists.length ? instanceArtists : eventArtists;

    instance.artists = [];

    for (const artist of artists) {
        if (!artist?.person?.id) {
            continue;
        }

        const relatedArtists = [];

        if (artist.related_artists && isArray(artist.related_artists)) {
            for (const related of artist.related_artists) {
                if (!related?.person?.id) {
                    continue;
                }

                let url = related.override_url || related.person.url;

                // The bio/blocks flags live on the person, not on the programme row
                if (!related.override_url && !related.person.hasBiography && !related.person.hasBlocks) {
                    url = false;
                }

                if (related.suppress_link) {
                    url = false;
                }

                relatedArtists.push({
                    id: related.person.id,
                    name: related.person.name,
                    firstName: related.person.firstName,
                    lastName: related.person.lastName,
                    attributes: {
                        custom: {
                            is_deceased: related.person?.attributes?.custom?.is_deceased
                        },
                    },
                    role: related.role || related.person.role,
                    url,
                });
            }
        }

        let url = artist.override_url || artist.person.url;

        if (!artist.override_url && !artist.person.hasBiography && !artist.person.hasBlocks) {
            url = false;
        }

        if (artist.suppress_link) {
            url = false;
        }

        instance.artists.push({
            id: artist.person.id,
            name: artist.person.name,
            firstName: artist.person.firstName,
            lastName: artist.person.lastName,
            attributes: {
                custom: {
                    is_deceased: artist.person?.attributes?.custom?.is_deceased
                },
            },
            role: artist.role || artist.person.role,
            url,
            relatedArtists,
        });
    }
}
